/**
 * BUSINESS PORTAL (Phase 5). Tier 3 KYB: the company and its people.
 *
 * KYB screens the PEOPLE, not just the company: a designated person incorporates a fresh company that
 * is on no list, so every director and beneficial owner is screened as an individual, and Tier 3 is
 * refused while any of them is unscreened or a hit. That is why this surface leads with the people.
 */

import { api } from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';
import { Reveal } from '@/components/motion';
import { StatusPill } from '@/components/status';

// KYB screening maps onto the shared status vocabulary: `clear` reads as "Screened" (positive).
function ScreeningPill({ status }: { status: string }) {
  return <StatusPill status={status} label={status === 'clear' ? 'Screened' : undefined} />;
}

export default function Business() {
  const { data, loading, error, reload } = useResource(() => api.business(), []);

  if (loading)
    return (
      <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
        <div className="space-y-6 lg:col-span-2">
          <LoadingCard lines={3} />
          <LoadingCard lines={3} />
        </div>
        <div className="space-y-6">
          <LoadingCard lines={2} />
        </div>
      </div>
    );
  if (error) return <ErrorState body="We could not load the business portal." onRetry={reload} />;
  if (!data) return null;

  const p = data.profile;

  return (
    <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
      <div className="space-y-6 lg:col-span-2">
        <header className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-page-title text-ink">{p.legal_name}</h1>
            <p className="mt-0.5 font-mono text-xs text-ink-subtle">{p.reg}</p>
          </div>
          <span className="shrink-0 rounded-pill bg-primary/10 px-2.5 py-0.5 text-micro font-semibold text-brand">
            {p.tier} &middot; {p.kyb_status}
          </span>
        </header>

        {/* DIRECTORS */}
        <section>
          <h2 className="mb-2 font-semibold text-ink">Directors</h2>
          <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
            {data.directors.map((d, i) => (
              <li key={i} className="flex items-center gap-3 px-4 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-brand">
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M15.75 8.25a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.5 20.25a7.5 7.5 0 0 1 15 0"
                    />
                  </svg>
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{d.name}</p>
                  <p className="text-xs text-ink-subtle">
                    {d.role} &middot; ID {d.id_ref}
                  </p>
                </div>
                <ScreeningPill status={d.screening} />
              </li>
            ))}
          </ul>
        </section>

        {/* BENEFICIAL OWNERS */}
        <section>
          <h2 className="mb-2 font-semibold text-ink">Beneficial owners</h2>
          <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
            {data.beneficialOwners.map((o, i) => (
              <li key={i} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{o.name}</p>
                  <p className="text-xs text-ink-subtle">{o.pct}% ownership</p>
                </div>
                <ScreeningPill status={o.screening} />
              </li>
            ))}
          </ul>
        </section>

        {/* SIGNATORIES */}
        <section>
          <h2 className="mb-2 font-semibold text-ink">Authorized signatories</h2>
          <ul className="space-y-3">
            {data.signatories.map((s, i) => (
              <li key={i} className="rounded-card border border-border bg-surface p-4 shadow-card">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-ink">{s.name}</p>
                    <p className="text-xs text-ink-subtle">{s.role} signatory</p>
                  </div>
                  <span className="text-xs text-ink-muted">{s.permissions.length} permissions</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {s.permissions.map((perm) => (
                    <span
                      key={perm}
                      className="rounded-pill bg-surface-sunken px-2 py-0.5 text-micro font-medium text-ink-muted"
                    >
                      {perm}
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-ink-subtle">
            A signatory permission can only narrow the business governance limits, never widen them.
          </p>
        </section>
      </div>

      {/* RAIL: wallet + KYB */}
      <div className="space-y-6">
        <Reveal>
          <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary to-primary-dark p-5 text-white shadow-lifted">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full bg-primary-light/40 blur-2xl"
            />
            <div className="relative">
              <p className="text-sm text-white/70">Business wallet</p>
              <p className="mt-1 flex items-baseline gap-2">
                <span className="text-sm font-medium text-white/70">KES</span>
                <span className="tabular text-3xl font-semibold tracking-tight">{p.wallet}</span>
              </p>
            </div>
          </section>
        </Reveal>

        <section className="rounded-card border border-border bg-surface p-5 shadow-card">
          <h2 className="font-semibold text-ink">Verification</h2>
          <p className="mt-2 text-sm text-ink-muted">
            This business is verified at {p.tier}. Every director and beneficial owner is screened
            as an individual, which is what verifying the company alone cannot do.
          </p>
          <button
            type="button"
            disabled
            title="Available in a later sandbox phase"
            className="mt-4 w-full cursor-not-allowed rounded-field border border-border py-2 text-sm font-medium text-ink-muted opacity-60"
          >
            Manage team &amp; permissions (coming soon)
          </button>
        </section>
      </div>
    </div>
  );
}
